import type { ReactNode } from 'react'
import type { TemplateField } from '@/config/deployment/deployment.types'
import { getCapabilityPolicy } from '@/store/deployment.store'
import { useDeploymentStore } from '@/store/deployment.store'
import './template-field-gate.scss'

export type TemplateFieldGateState = {
  enabled: boolean
  hidden: boolean
  readOnly: boolean
  message?: string
}

type TemplateFieldGateProps = {
  field: TemplateField
  children: ReactNode | ((state: TemplateFieldGateState) => ReactNode)
  fallback?: ReactNode
}

/**
 * Controls a field or section in the template editor.
 *
 * Unlike CapabilityGate, missing field configuration is deliberately treated as
 * enabled: deployment profiles only need to describe exceptions to the default.
 */
export function TemplateFieldGate({ field, children, fallback = null }: TemplateFieldGateProps) {
  const setting = useDeploymentStore(state => state.profile.agent.templates.fields[field])
  const policy =
    setting === undefined
      ? { enabled: true, unavailableBehavior: 'readonly' as const }
      : getCapabilityPolicy(setting)
  const state: TemplateFieldGateState = {
    enabled: policy.enabled,
    hidden: !policy.enabled && policy.unavailableBehavior === 'hidden',
    readOnly: !policy.enabled && policy.unavailableBehavior !== 'hidden',
    message: policy.message
  }
  const content = typeof children === 'function' ? children(state) : children

  if (state.hidden) {
    return <>{fallback}</>
  }

  if (!state.readOnly) {
    return <>{content}</>
  }

  return (
    <fieldset
      disabled
      aria-readonly="true"
      title={state.message}
      className="template-field-gate--readonly min-w-0 border-0 p-0 m-0"
    >
      {content}
    </fieldset>
  )
}
